import useQuickAction from "../../hooks/useQuickAction";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { PlusIcon } from "@heroicons/react/solid";
import AdvanceShiftEditing from "./AdvanceShiftEditing";
import { formatTime } from "../../utility";
import usePut from "../../hooks/usePut";
import Modals from "../../components/modal/Modals";
import toast from "react-hot-toast";
import useGet from "../../hooks/read/useGet";
import useAwaitGet from "../../hooks/read/useAwaitGet";
import { Button } from "../../components/ui";
import Spinner from "../../components/loader/Spinner";
import SearchBar from "../../components/inputs/SearchBar";
import Pagination from "../../components/pagination";
import RoundedContainer from "../../components/containers/RoundedContainer";
import PrimaryLoader from "../../components/common/PrimaryLoader";

const LIMIT = 12;

const Shifts = () => {
  const [isOpenAdvanceShift, setIsOpenAdvanceShift] = useState(false);
  const [isOpenShiftDelete, setIsOpenShiftDelete] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [mode, setMode] = useState(null);

  const getSingleShift = useAwaitGet({
    url: "get_single_shift",
    onSuccess: () => {
      setIsOpenAdvanceShift(true);
    },
    onError: (err) => {
      const { status, data } = err.response || {};
      if (status >= 400 || status <= 499) {
        toast.error(data?.message);
      } else {
        toast.error("Server error");
      }
    },
  });
  const { handleAuth: deleteShift } = usePut({
    url: "shift_disabled",
    refreshUrl: "shift_listing",
    onSuccess: () => {
      setIsOpenShiftDelete(false);
      toast.success("Shift Deleted Successfully");
    },
    onError: () => {
      setIsOpenShiftDelete(false);
      toast.error("There Was An Error While Deleting Please Try Again Later");
    },
  });
  const handleDelete = () => {
    let data = { id: selectedShiftId };
    deleteShift.mutate(data);
  };
  const handleCreateShift = () => {
    setMode(null);
    setIsOpenAdvanceShift(true);
  };
  useQuickAction("add-shift", handleCreateShift);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageNo = Number(searchParams.get("page_number")) || 0;
  const searchQuery = searchParams.get("search_query") || "";
  const [search, setSearch] = useState(searchQuery);
  const [prevUrlSearch, setPrevUrlSearch] = useState(searchQuery);

  // Keep the search box in sync with the URL (back/forward).
  if (searchQuery !== prevUrlSearch) {
    setSearch(searchQuery);
    setPrevUrlSearch(searchQuery);
  }

  const { list } = useGet({
    url: "shift_listing",
    params: { limit: LIMIT, search_query: searchQuery.trim(), page_number: pageNo },
  });
  const shifts = list.data?.data || [];

  // Empty values are dropped from the URL.
  const updateParams = (changes, options) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key)
    );
    setSearchParams(next, options);
  };

  // Searching goes back to the first page.
  const debouncedSearch = useDebouncedCallback((value) => {
    updateParams({ search_query: value, page_number: 0 }, { replace: true });
  }, 300);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handlePageChange = ({ selected }) => {
    updateParams({ page_number: selected });
  };

  const handleDeleteModal = (id) => {
    setSelectedShiftId(id);
    setIsOpenShiftDelete(true);
  };
  const handleClickedAdvanceShift = (id) => {
    let data = { id };
    setMode("edit");
    getSingleShift.mutate(data);
  };

  return (
    <>
      {getSingleShift.isLoading && <Spinner />}
      <div className="p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <SearchBar
              placeholder="Search by shift name..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <Button className={"px-4 py-2"} onClick={handleCreateShift}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add New Shift
          </Button>
        </div>
        {list.isLoading ? (
          <div className="flex justify-center items-center min-h-96">
            <PrimaryLoader />
          </div>
        ) : list.isError ? (
          <div className="text-center py-16 bg-white rounded-md border border-gray-200 mt-5">
            <p className="text-gray-700 text-2xl font-semibold mb-2">Couldn't Load Shifts</p>
            <p className="text-gray-500 text-sm mb-5">
              Something went wrong while fetching your shifts.
            </p>
            <Button variant="outline" onClick={() => list.refetch()}>
              Try Again
            </Button>
          </div>
        ) : !shifts.length && searchQuery ? (
          <div className="text-center py-16 bg-white rounded-md border border-gray-200 mt-5">
            <p className="text-gray-700 text-2xl font-semibold mb-2">No Shifts Found</p>
            <p className="text-gray-500 text-sm">
              No shifts match "{searchQuery}". Try a different search.
            </p>
          </div>
        ) : !shifts.length ? (
          <div className="text-center py-16 bg-white rounded-md border border-gray-200 mt-5">
            <p className="text-gray-700 text-2xl font-semibold mb-2">No Shifts To Show</p>
            <p className="text-gray-500 text-sm mb-5">Get started by creating your company's work shifts.</p>
            <Button onClick={handleCreateShift}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add New Shift
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-5 mt-5">
              {shifts.map((shiftData, shiftIdx) => {
                const {
                  id,
                  shiftName,
                  shiftInTime,
                  shiftOutTime,
                  workingTypeMode,
                  working_hours,
                  maximum_working_hours,
                  minimum_working_hours,
                } = shiftData;
                return (
                  <div
                    key={shiftIdx}
                    className="w-full rounded-md border border-gray-300 p-4"
                  >
                    <div>
                      <p className="">
                        <label className="text-gray-700 font-semibold text-lg">
                          Shift Name:{" "}
                        </label>
                        <span>{shiftName}</span>
                      </p>
                      {workingTypeMode === "lenient" ? (
                        <div className="">
                          <p>
                            <label className="text-gray-700 font-semibold text-xs">
                              Maximum working hour:{" "}
                            </label>
                            <span className="text-gray-700 font-semibold text-xs">
                              {maximum_working_hours} hr
                            </span>
                          </p>
                          <p>
                            <label className="text-gray-700 font-semibold text-xs">
                              Minimum working hour:{" "}
                            </label>
                            <span className="text-gray-700 font-semibold text-xs">
                              {minimum_working_hours} hr
                            </span>
                          </p>
                        </div>
                      ) : (
                        <p>
                          <label className="text-gray-700 font-semibold text-lg">
                            Shift Time:{" "}
                          </label>
                          <span className="">
                            <span className="mx-1">{shiftInTime && formatTime(shiftInTime)}</span>
                            {shiftInTime && shiftOutTime && "-"}
                            <span className="mx-1">{shiftOutTime && formatTime(shiftOutTime)}</span>
                          </span>
                        </p>
                      )}
                      <div className=" space-x-2 mt-4">
                        <Button
                          variant={"link"}
                          className={"px-0 py-2"}
                          onClick={() => handleClickedAdvanceShift(id)}
                        >
                          <span className=" text-primary font-semibold text-xs">
                            View Shift Detail
                          </span>
                        </Button>
                        {/* <Button variant={'ghost'} className={'px-4 py-2'} onClick={() => handleDeleteModal(id)}>
                                            <span className=' text-red-600 font-semibold'>
                                                Delete
                                            </span>
                                        </Button> */}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <RoundedContainer className="mt-5">
              <Pagination
                lengthofItems={list.data?.total_count}
                limit={LIMIT}
                currentPage={pageNo}
                onPageChange={handlePageChange}
              />
            </RoundedContainer>
          </>
        )}
      </div>
      {/* {console.log(getSingleShift.data?.data[0])} */}
      {isOpenAdvanceShift && (
        <AdvanceShiftEditing
          mode={mode}
          {...(mode === "edit" && { shiftData: getSingleShift.data?.data })}
          open={isOpenAdvanceShift}
          onClose={() => setIsOpenAdvanceShift(false)}
        />
      )}
      <Modals show={isOpenShiftDelete}>
        <div>
          <p className=" roboto text-center font-bold text-2xl text-gray-700">
            Are you sure you want to delete this shift?
          </p>
          <div className=" mt-12 space-x-3 text-center ">
            <Button
              className="font-semibold bg-red-200 text-red-700 border-red-800"
              onClick={handleDelete}
            >
              Yes
            </Button>
            <Button
              className="font-semibold"
              variant={"outline"}
              onClick={() => setIsOpenShiftDelete(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modals>
    </>
  );
};

export default Shifts;
